// src/pages/signagepage.js
// Digital Signage Campaign Manager — "Carteleria Digital"
//
// Fase 2 of the revamp: 2 tabs (Estado, Campañas). The master switch,
// the schedule, and the today-rotor live on the Estado tab. All
// campaigns CRUD live on the Campañas tab.
import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Tag, Modal as AntModal, message } from 'antd';
import {
    PlayCircleOutlined,
    AppstoreOutlined,
    PlusOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { BsFileEarmarkPlayFill, BsPlayCircle, BsStopCircle } from 'react-icons/bs';
import { MdEdit, MdDelete, MdVisibility } from 'react-icons/md';

import CampaignModal from '../components/signage/CampaignModal';
import CampaignPlayer from '../components/signage/CampaignPlayer';
import CampaignPreview from '../components/signage/CampaignPreview';
import EstadoDeCarteleria from '../components/signage/EstadoDeCarteleria';
import SignageCalendarStrip from '../components/signage/SignageCalendarStrip';
import TodayCampaignsList from '../components/signage/TodayCampaignsList';
import { AuthImage, AuthVideo } from '../components/signage/AuthMedia';
import {
    getAllCampaigns,
    getCampaignById,
    deleteCampaign,
    enableCampaign,
    disableCampaign,
    enableSystem,
    disableSystem,
    getSystemStatus,
    getTodaysCampaigns,
    isCampaignScheduledNow,
    argWeekday,
} from '../services/signageService';
import { useAuth } from '../context/AuthContext';

import './signagepage.css';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
Alert.displayName = 'SignagePageAlert';

const DAY_LABELS = {
    lunes: 'Lun', martes: 'Mar', miercoles: 'Mié',
    jueves: 'Jue', viernes: 'Vie', sabado: 'Sáb', domingo: 'Dom',
};

const ALL_DAYS = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];

const DAY_KEY_TO_WEEKDAY = {
    lunes: 1, martes: 2, miercoles: 3, jueves: 4, viernes: 5, sabado: 6, domingo: 0,
};

function SignagePage() {
    const { user: authUser } = useAuth();
    const [campaigns, setCampaigns] = useState([]);
    const [systemStatus, setSystemStatus] = useState(null);
    const [today, setToday] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [playingCampaign, setPlayingCampaign] = useState(null);
    const [previewCampaign, setPreviewCampaign] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [activeTab, setActiveTab] = useState('estado');

    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    // ── Load campaigns + system + today ──
    // Note: GET /signage/campaigns returns summary data (no slides array).
    // Full campaign (with slides) is fetched on demand via getCampaignById.
    const loadCampaigns = useCallback(async () => {
        const list = await getAllCampaigns();
        // Fetch full details (with slides) for all campaigns in parallel
        const fullCampaigns = await Promise.all(
            list.map(async (c) => {
                try {
                    const full = await getCampaignById(c.id);
                    return full || c;
                } catch { return c; }
            })
        );
        setCampaigns(fullCampaigns);
    }, []);

    const loadSystemAndToday = useCallback(async () => {
        try {
            const [status, todayPayload] = await Promise.all([
                getSystemStatus(),
                getTodaysCampaigns().catch(() => ({ today: [] })),
            ]);
            setSystemStatus(status || { enabled: false, paused: false, todayCount: 0 });
            setToday(todayPayload?.today || []);
        } catch {
            // soft-fail: keep previous state
        }
    }, []);

    const loadAll = useCallback(async () => {
        await Promise.all([loadCampaigns(), loadSystemAndToday()]);
    }, [loadCampaigns, loadSystemAndToday]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    // ── CRUD Handlers ──
    const handleSaveCampaign = useCallback(async () => {
        try {
            showSnackbar('Campaña guardada', 'success');
            setIsModalOpen(false);
            setEditingCampaign(null);
            await loadAll();
        } catch (err) {
            showSnackbar('Error al guardar: ' + err.message, 'error');
        }
    }, [loadCampaigns, showSnackbar]);

    const handleDelete = useCallback((campaign) => {
        AntModal.confirm({
            title: '¿Eliminar campaña?',
            icon: <ExclamationCircleOutlined />,
            content: `Se eliminará "${campaign.name}" permanentemente.`,
            okText: 'Eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                await deleteCampaign(campaign.id);
                showSnackbar('Campaña eliminada', 'info');
                await loadAll();
            },
        });
    }, [loadCampaigns, showSnackbar]);

    const handleToggleCampaign = useCallback(async (campaignId, currentEnabled) => {
        try {
            if (currentEnabled) {
                await disableCampaign(campaignId);
                showSnackbar('Campaña quitada del rotativo', 'info');
            } else {
                await enableCampaign(campaignId);
                showSnackbar('Campaña agregada al rotativo', 'success');
            }
            await loadAll();
        } catch (err) {
            showSnackbar('Error: ' + (err.response?.data?.error || err.message), 'error');
        }
    }, [loadAll, showSnackbar]);

    const handleMasterSwitch = useCallback(async (nextEnabled) => {
        try {
            if (nextEnabled) {
                const r = await enableSystem();
                showSnackbar(r.message || 'Cartelería activada', 'success');
            } else {
                const r = await disableSystem();
                showSnackbar(r.message || 'Cartelería desactivada', 'info');
            }
            await loadSystemAndToday();
        } catch (err) {
            showSnackbar('Error al desactivar: ' + (err.response?.data?.error || err.message), 'error');
        }
    }, [showSnackbar]);

    const handleEdit = useCallback(async (campaign) => {
        try {
            const full = await getCampaignById(campaign.id);
            setEditingCampaign(full || campaign);
            setIsModalOpen(true);
        } catch {
            setEditingCampaign(campaign);
            setIsModalOpen(true);
        }
    }, []);

    const handleCreate = useCallback(() => {
        if (isDemoMode) { message.info('Nueva Campaña — disponible en la versión completa del sistema'); return; }
        setEditingCampaign(null);
        setIsModalOpen(true);
    }, []);

    const handlePlay = useCallback(async (campaign) => {
        try {
            const full = await getCampaignById(campaign.id);
            setPlayingCampaign(full || campaign);
        } catch {
            setPlayingCampaign(campaign);
        }
    }, []);

    const handleExitPlayer = useCallback(() => {
        setPlayingCampaign(null);
    }, []);

    // ── Derived data ──
    // today in ART
    const todayWd = argWeekday();
    const todayDayKey = ALL_DAYS[
        Object.entries(DAY_KEY_TO_WEEKDAY).find(([, wd]) => wd === todayWd)?.[0] === undefined
            ? 0
            : ALL_DAYS.indexOf(Object.entries(DAY_KEY_TO_WEEKDAY).find(([, wd]) => wd === todayWd)[0])
    ];

    const getScheduleForDay = useCallback((dayKey) => {
        return campaigns.filter(c => {
            const days = c.schedule?.days || [];
            return days.length === 0 || days.includes(dayKey);
        });
    }, [campaigns]);

    // ── Tab: Estado ──
    const renderEstado = () => (
        <div>
            <EstadoDeCarteleria
                systemStatus={systemStatus}
                setSystemStatus={setSystemStatus}
                todayCount={today.length}
                onSnackbar={showSnackbar}
            />
            <SignageCalendarStrip campaigns={campaigns} />
            <TodayCampaignsList
                today={today}
                onPlay={handlePlay}
                onEdit={handleEdit}
                onJumpToCampanas={() => setActiveTab('campanas')}
            />
        </div>
    );

    // ── Tab: Calendar ──
    // ── Tab: Campañas (was "All Campaigns") ──
    const renderAllCampaigns = () => (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>Campañas Guardadas ({campaigns.length})</h3>
                <Button
                    variant="contained"
                    startIcon={<PlusOutlined />}
                    onClick={handleCreate}
                    sx={{ textTransform: 'none' }}
                >
                    Nueva Campaña
                </Button>
            </div>

            {campaigns.length === 0 ? (
                <div className="signage-empty-state">
                    <BsFileEarmarkPlayFill className="empty-icon" />
                    <h3>No hay campañas</h3>
                    <p>Creá tu primera campaña de cartelería digital.</p>
                    <Button variant="contained" startIcon={<PlusOutlined />} onClick={handleCreate} sx={{ textTransform: 'none' }}>
                        Crear Campaña
                    </Button>
                </div>
            ) : (
                <div className="signage-campaigns-grid">
                    {campaigns.map(campaign => (
                        <div
                            key={campaign.id}
                            className={`signage-campaign-card ${campaign.isEnabled === false ? 'disabled' : ''}`}
                        >
                            {/* Thumbnails strip */}
                            <div className="signage-campaign-card-thumbnails">
                                {(campaign.slides || []).slice(0, 5).map(slide => (
                                    slide.type === 'video' ? (
                                        <AuthVideo key={slide.id} src={slide.src} muted style={{ pointerEvents: 'none' }} />
                                    ) : (
                                        <AuthImage key={slide.id} src={slide.thumbnail || slide.src} alt={slide.filename} />
                                    )
                                ))}
                                {(!campaign.slides || campaign.slides.length === 0) && (
                                    <div style={{
                                        flex: 1, display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', color: '#666'
                                    }}>
                                        Sin slides
                                    </div>
                                )}
                            </div>

                            <div className="signage-campaign-card-body">
                                <h4>
                                    {campaign.name}
                                    {isCampaignScheduledNow(campaign) && (
                                        <Tag color="green" style={{ fontSize: 10 }}>HOY</Tag>
                                    )}
                                    {campaign.isEnabled === false && (
                                        <Tag color="default" style={{ fontSize: 10 }}>Quitada</Tag>
                                    )}
                                </h4>
                                <p>{campaign.description || 'Sin descripción'}</p>

                                <div className="signage-campaign-card-meta">
                                    <Tag>{campaign.orientation === 'portrait' ? 'Vertical' : 'Horizontal'}</Tag>
                                    <Tag>{campaign.slides?.length || campaign.slidesCount || 0} slides</Tag>
                                    {campaign.schedule?.days?.length > 0 ? (
                                        <Tag color="blue">
                                            {campaign.schedule.days.map(d => DAY_LABELS[d]).join(', ')}
                                        </Tag>
                                    ) : (
                                        <Tag color="blue">Todos los días</Tag>
                                    )}
                                </div>

                                <div className="signage-campaign-card-actions">
                                    <Button
                                        variant={campaign.isEnabled === false ? 'outlined' : 'contained'}
                                        size="small"
                                        color={campaign.isEnabled === false ? 'primary' : 'error'}
                                        onClick={() => handleToggleCampaign(campaign.id, campaign.isEnabled !== false)}
                                        sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                                    >
                                        {campaign.isEnabled === false ? 'Habilitar' : 'Quitar del rotativo'}
                                    </Button>
                                    <IconButton
                                        size="small"
                                        onClick={() => handlePlay(campaign)}
                                        title="Reproducir"
                                    >
                                        <BsPlayCircle />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={async () => {
                                            try {
                                                const full = await getCampaignById(campaign.id);
                                                setPreviewCampaign(full || campaign);
                                            } catch { setPreviewCampaign(campaign); }
                                        }}
                                        title="Vista Previa"
                                    >
                                        <MdVisibility />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleEdit(campaign)}
                                        title="Editar"
                                    >
                                        <MdEdit />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDelete(campaign)}
                                        title="Eliminar"
                                        color="error"
                                        disabled={isDemoMode}
                                    >
                                        <MdDelete />
                                    </IconButton>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // ── Tab items ──
    const tabItems = [
        {
            key: 'estado',
            label: <span><PlayCircleOutlined /> Estado</span>,
            children: renderEstado(),
        },
        {
            key: 'campanas',
            label: <span><AppstoreOutlined /> Campañas</span>,
            children: renderAllCampaigns(),
        },
    ];

    return (
        <>
            <div className="signage-page">
                <div className="signage-page-header">
                    <BsFileEarmarkPlayFill size={36} style={{ color: '#333' }} />
                    <h2>CARTELERÍA DIGITAL</h2>
                </div>

                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                    className="signage-tabs"
                />
            </div>

            {/* Create / Edit Modal */}
            <CampaignModal
                open={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingCampaign(null); }}
                onSaved={handleSaveCampaign}
                editCampaign={editingCampaign}
                allCampaigns={campaigns}
            />

            {/* Fullscreen Player */}
            {playingCampaign && (
                <CampaignPlayer
                    campaign={playingCampaign}
                    onExit={handleExitPlayer}
                    loop={true}
                />
            )}

            {/* Preview Modal (inline) */}
            {previewCampaign && (
                <AntModal
                    open={true}
                    title={`Vista Previa: ${previewCampaign.name}`}
                    onCancel={() => setPreviewCampaign(null)}
                    footer={null}
                    width={previewCampaign.orientation === 'portrait' ? 380 : 560}
                    centered
                >
                    <CampaignPreview campaign={previewCampaign} />
                </AntModal>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default SignagePage;